import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, FileText, CalendarClock, Users2, Gavel, Lock, ChevronLeft } from "lucide-react";

/**
 * Resident-facing minutes archive. Reads /api/board/minutes/public which
 * strips per-director votes but keeps motion text, pass/fail result, and
 * yes/no/abstain tally. Debounced search (350ms) hits the server so we can
 * scale to big archives without loading every minute file up-front.
 */
function useDebounced(value, delay) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

function MeetingCard({ m, onOpen }) {
  return (
    <button
      onClick={() => onOpen(m.id)}
      className="text-left w-full rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] p-4 transition"
      data-testid={`minute-${m.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <CalendarClock className="h-3 w-3" />
            {new Date(m.scheduled_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            <span>·</span>
            <span className="capitalize">{m.type}</span>
          </div>
          <div className="font-medium mt-1 truncate">{m.title}</div>
          {m.minutes_excerpt && (
            <div className="text-xs text-zinc-400 mt-2 line-clamp-2">{m.minutes_excerpt}…</div>
          )}
          <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-500">
            <span className="inline-flex items-center gap-1"><Users2 className="h-3 w-3" /> {(m.attendees || []).filter((a) => a.attended).length} directors</span>
            <span className="inline-flex items-center gap-1"><Gavel className="h-3 w-3" /> {(m.motions || []).length} motions</span>
          </div>
        </div>
        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 whitespace-nowrap">
          <Lock className="h-3 w-3 mr-1" /> Finalized
        </Badge>
      </div>
    </button>
  );
}

function MinuteDetail({ mid, onBack }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/board/minutes/public/${mid}`).then(({ data }) => alive && setM(data)).catch(() => alive && setM(false));
    return () => { alive = false; };
  }, [mid]);
  if (m === null) return <Card className="border-white/10 bg-card p-6 text-sm text-muted-foreground">Loading…</Card>;
  if (!m) return <Card className="border-white/10 bg-card p-6 text-sm text-rose-300">Couldn&apos;t load these minutes.</Card>;

  return (
    <Card className="border-white/10 bg-card p-6 space-y-5" data-testid="minute-detail">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" className="border-white/10" onClick={onBack} data-testid="minute-back">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to archive
        </Button>
        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
          <Lock className="h-3 w-3 mr-1" /> Hash-locked {m.minutes_hash?.slice(0, 8)}…
        </Badge>
      </div>

      <div>
        <div className="overline text-[10px]">{m.type} meeting</div>
        <h1 className="text-xl font-semibold mt-1" data-testid="minute-title">{m.title}</h1>
        <div className="text-xs text-zinc-500 mt-1">
          {new Date(m.scheduled_at).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
        </div>
      </div>

      <div>
        <div className="overline text-xs mb-2 flex items-center gap-2"><Users2 className="h-3 w-3" /> Attendance</div>
        <div className="flex flex-wrap gap-1">
          {m.attendees.filter((a) => a.attended).map((a, i) => (
            <Badge key={i} variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">{a.name}</Badge>
          ))}
          {m.attendees.filter((a) => a.attended).length === 0 && (
            <span className="text-xs text-muted-foreground">No attendance recorded.</span>
          )}
        </div>
      </div>

      {m.motions.length > 0 && (
        <div>
          <div className="overline text-xs mb-2 flex items-center gap-2"><Gavel className="h-3 w-3" /> Motions ({m.motions.length})</div>
          <div className="space-y-2">
            {m.motions.map((mo) => (
              <div key={mo.id} className="rounded border border-white/10 bg-white/[0.02] p-3">
                <div className="text-sm">{mo.text}</div>
                <div className="flex items-center gap-2 mt-2 text-[11px]">
                  <Badge variant="outline" className={mo.result === "passed" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : mo.result === "failed" ? "border-rose-500/40 text-rose-300 bg-rose-500/10" : "border-white/10"}>{mo.result}</Badge>
                  <span className="text-emerald-300">Yes {mo.tally.yes}</span>
                  <span className="text-rose-300">No {mo.tally.no}</span>
                  <span className="text-zinc-400">Abstain {mo.tally.abstain}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="overline text-xs mb-2 flex items-center gap-2"><FileText className="h-3 w-3" /> Minutes</div>
        <div className="rounded border border-white/10 bg-white/[0.02] p-4 whitespace-pre-wrap font-sans text-sm leading-relaxed" data-testid="minute-body">
          {m.minutes_final}
        </div>
      </div>
    </Card>
  );
}

export default function ResidentBoardMinutesPage() {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 350);
  const [year, setYear] = useState(null);
  const [openMid, setOpenMid] = useState(null);
  const [data, setData] = useState({ items: [], years: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dq) params.set("q", dq);
      if (year) params.set("year", year);
      const { data } = await api.get(`/board/minutes/public${params.toString() ? "?" + params.toString() : ""}`);
      setData(data);
    } catch {
      setData({ items: [], years: [], total: 0 });
    }
    setLoading(false);
  }, [dq, year]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    for (const m of data.items) {
      const y = (m.scheduled_at || "").slice(0, 4) || "Undated";
      g[y] = g[y] || [];
      g[y].push(m);
    }
    return g;
  }, [data.items]);

  if (openMid) return (
    <div>
      <PageHeader eyebrow="Governance" title="Board Minutes" subtitle="Read-only, hash-locked minutes of your community's board meetings." />
      <MinuteDetail mid={openMid} onBack={() => setOpenMid(null)} />
    </div>
  );

  return (
    <div>
      <PageHeader eyebrow="Governance" title="Board Minutes" subtitle="Every finalized board meeting your directors have published. Search or filter by year." />

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid="minutes-search"
            className="pl-9 bg-white/[0.04] border-white/10"
            placeholder="Search across minutes, titles, motions…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setYear(null)}
            data-testid="year-all"
            className={`text-[11px] px-3 py-1 rounded-full border ${!year ? "border-orange-500/40 text-orange-300 bg-orange-500/10" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
          >All</button>
          {(data.years || []).map((y) => (
            <button
              key={y}
              onClick={() => setYear(y === year ? null : parseInt(y, 10))}
              data-testid={`year-${y}`}
              className={`text-[11px] px-3 py-1 rounded-full border ${String(year) === String(y) ? "border-orange-500/40 text-orange-300 bg-orange-500/10" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
            >{y}</button>
          ))}
        </div>
        {loading && <span className="text-xs text-zinc-500">Searching…</span>}
      </div>

      {data.items.length === 0 ? (
        <Card className="border-white/10 bg-card p-8 text-center">
          <FileText className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
          <div className="text-sm text-muted-foreground">
            {q ? `No minutes match "${q}". Try a broader search.` : "No published minutes yet — check back after the next board meeting."}
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([y, ms]) => (
            <div key={y}>
              <div className="overline text-xs text-zinc-500 mb-2">{y} · {ms.length} meeting{ms.length === 1 ? "" : "s"}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ms.map((m) => <MeetingCard key={m.id} m={m} onOpen={setOpenMid} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
